import React, { useState } from 'react';
import PropTypes from 'prop-types';
import posed from 'react-pose';
import _styles from './styles.module.css';
import Box from '../Box';

const AnimatedContents = posed.div({
  closed: {
    height: 0,
    opacity: 0,
    transition: ({ delay }: any) => ({ delay }),
    applyAtEnd: { display: 'none' },
  },
  open: {
    height: 'auto',
    opacity: 1,
    transition: ({ delay }: any) => ({ delay }),
    applyAtStart: { display: 'block' },
  },
});

(AnimatedContents as any).displayName = 'AnimatedContents';

const StaticContents = ({ children, collapsed }: any) => (
  <Box display={collapsed ? 'none' : 'block'}>
    { children }
  </Box>
);

(StaticContents as any).displayName = 'StaticContents';

(StaticContents as any).propTypes = {
  children: PropTypes.node.isRequired,
  collapsed: PropTypes.bool,
};

(StaticContents as any).defaultProps = {
  collapsed: true,
};

const Collapsible = ({
  defaultCollapsed,
  children,
  trigger,
  styles,
  animated,
  animationDelay,
}: any) => {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  const userPrefersReducedMotion = typeof window !== 'undefined'
    && typeof (window as any).matchMedia === 'function'
    && (window as any).matchMedia('(prefers-reduced-motion)').matches;

  const animationDisabled = !animated || userPrefersReducedMotion;

  return (
    <div className={styles.collapsible}>
      {
        trigger({
          collapsed,
          toggleCollapsible: () => {
            setCollapsed((prevCollapsed?: any) => !prevCollapsed);
          },
        })
      }
      {
        animationDisabled ? (
          <StaticContents collapsed={collapsed}>
            { typeof children === 'function' ? children(collapsed) : children }
          </StaticContents>
        ) : (
          <AnimatedContents
            pose={collapsed ? 'closed' : 'open'}
            withParent={false}
            delay={animationDelay}
            className={styles.animatedContents}
          >
            { typeof children === 'function' ? children({ collapsed }) : children }
          </AnimatedContents>
        )
      }
    </div>
  );
};

(Collapsible as any).displayName = 'Collapsible';

(Collapsible as any).propTypes = {
  /**
    * The contents of the collapsible that can be hidden or displayed
    */
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,
  /**
    * The initial collapsed state of the Collapsible. If false, it'll render expanded initially.
    */
  defaultCollapsed: PropTypes.bool,
  /**
    * A function to render to the trigger. It is given `collapsed` (boolean) and
    `toogleCollapsible` (function) as render props.
    */
  trigger: PropTypes.func.isRequired,
  /**
    * The CSS classNames for this component. NEVER manually specify this prop
    */
  styles: PropTypes.shape({}),
  /**
    * Disables animation
    */
  animated: PropTypes.bool,
  /**
    * Applies animation delay
    */
  animationDelay: PropTypes.number,
};

(Collapsible as any).defaultProps = {
  styles: _styles,
  defaultCollapsed: true,
  animated: true,
  animationDelay: 0,
};

export default Collapsible;
